import { formatTimeSpacedAmPm } from '../formatting/timeFormatting';

/**
 * Calculates the time difference between two timestamps in minutes
 * @param {string|Date} start - The start timestamp
 * @param {string|Date} end - The end timestamp
 * @returns {number} - The time difference in minutes, rounded down to the nearest minute
 */
export const calculateTimeDifference = (start, end) => {
  if (!start || !end) return 0;

  // convert the timestamps to Date objects
  const startDate = new Date(start);
  const endDate = new Date(end);

  // calculate the difference in minutes and round down
  return Math.floor((endDate - startDate) / (1000 * 60));
};

/**
 * Formats a time range with start and end times in AM/PM format
 * @param {Date|string} start - Start time
 * @param {Date|string} end - End time
 * @returns {string} Formatted time range (e.g. "11:30 AM - 12:00 PM")
 */
export const formatTimeRangeToAmPm = (start, end) => {
  return `${formatTimeSpacedAmPm(start)}-${formatTimeSpacedAmPm(end)}`;
};

/**
 * Calculates the difference in minutes between now and a target time
 * @param {Date|string} targetTime - The target time to compare against
 * @returns {number} Difference in minutes
 */
export const getMinutesDifference = (targetTime) => {
  const now = new Date();
  const target = new Date(targetTime);
  return Math.round((target - now) / 60000);
};

/**
 * Formats minutes into a human-readable format
 * @param {number} minutes - The number of minutes to format
 * @returns {string} Formatted string like "1 hour 5 minute" or "45 minutes"
 */
export const formatMinutesToHumanReadable = (minutes) => {
  if (!minutes || minutes === 0) return '0 minutes';

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) {
    return `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
  }

  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }

  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
};

export const convertMinutesToMilliseconds = (minutes) => {
  return minutes * 60000;
};
